import { Bar } from "react-chartjs-2"
import { Chart, registerables, TooltipItem, TooltipModel } from "chart.js"

import { ApiTypes } from "types"

import getLegendMarginPlugin from "utils/chartjs/getLegendMarginPlugin"
import { useWindowResize } from "hooks/useWindowResize"

import { externalTooltipHandler } from "./CustomModesBarTooltip"
import { formatMonitoringXAxisTick, getGroupedTimestampLabels, getXAxisTickFormat } from "./formatMonitoringXAxisTick"
import { BarChartSettings } from "./getBarChartsSettings"
import parseFormattedValueToTime from "./parseFormattedValueToTime"
import { RuntimeBoxes } from "./RuntimeBoxes"
import { Title } from "./styled"

import styles from "./index.module.scss"

Chart.register(...registerables)

interface TemperatureBarChartProps {
  barChartSettings: BarChartSettings
  historicalData: ApiTypes.Res.MonitoringHistoricalDataBucket[]
  timeZone: string
}

const TemperatureBarChart = ({ barChartSettings, historicalData, timeZone }: TemperatureBarChartProps) => {
  const ticks = barChartSettings.ticksCount
  const isLargeScreen = useWindowResize()

  const barChartsData = (
    historicalData.reduce(
      (acc, item, index) => {
        if (index % barChartSettings.intervalItemsCount === 0) {
          acc.push({
            coolingStage1: 0,
            coolingStage2: 0,
            heatingStage1: 0,
            heatingStage2: 0,
            heatingStage3: 0,
          })
        }
        const lastItem = acc[acc.length - 1]
        lastItem.coolingStage1 += item["filters#current_cooling_stage_count"].buckets.count1.doc_count
        lastItem.coolingStage2 += item["filters#current_cooling_stage_count"].buckets.count2.doc_count
        lastItem.heatingStage1 += item["filters#current_heating_stage_count"].buckets.count1.doc_count
        lastItem.heatingStage2 += item["filters#current_heating_stage_count"].buckets.count2.doc_count
        lastItem.heatingStage3 += item["filters#current_heating_stage_count"].buckets.count3.doc_count

        return acc
      },
      [] as {
        coolingStage1: number
        coolingStage2: number
        heatingStage1: number
        heatingStage2: number
        heatingStage3: number
      }[],
    ) || []
  ).map(item => ({
    coolingStage1: item.coolingStage1 / barChartSettings.hourlyDivider,
    coolingStage2: item.coolingStage2 / barChartSettings.hourlyDivider,
    heatingStage1: item.heatingStage1 / barChartSettings.hourlyDivider,
    heatingStage2: item.heatingStage2 / barChartSettings.hourlyDivider,
    heatingStage3: item.heatingStage3 / barChartSettings.hourlyDivider,
  }))

  const labels = getGroupedTimestampLabels(historicalData, barChartSettings.intervalItemsCount, ticks)
  const shouldRotateXAxisLabels =
    (barChartSettings.labelX === "Hours" && ticks >= 24) || (barChartSettings.labelX === "Days" && ticks >= 30)
  const xAxisLabelRotation = shouldRotateXAxisLabels ? 45 : 0
  const data = {
    labels: labels,
    datasets: [
      {
        label: "Cooling - stage 1",
        backgroundColor: "#92c5fd",
        data: barChartsData.map(item => item.coolingStage1),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
      {
        label: "Cooling - stage 2",
        backgroundColor: "#2463eb",
        data: barChartsData.map(item => item.coolingStage2),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
      {
        label: "Heating - stage 1",
        backgroundColor: "#fca5a5",
        data: barChartsData.map(item => item.heatingStage1),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
      {
        label: "Heating - stage 2",
        backgroundColor: "#ef4444",
        data: barChartsData.map(item => item.heatingStage2),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
      {
        label: "Heating - stage 3",
        backgroundColor: "#b91c1c",
        data: barChartsData.map(item => item.heatingStage3),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
    ].filter(item => item.data.some(value => value !== 0)),
  }

  const options = {
    interaction: {
      mode: "index" as const,
    },
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: true,
        align: isLargeScreen ? ("end" as const) : ("start" as const),
        position: isLargeScreen ? ("top" as const) : ("bottom" as const),
        labels: {
          color: "#94A3B8",
          usePointStyle: true,
          pointStyle: "rect",
        },
      },
      tooltip: {
        enabled: false,
        position: "nearest" as const,
        external: (context: { chart: Chart; tooltip: TooltipModel<"bar"> }) => externalTooltipHandler(context, 200),
        callbacks: {
          label: function (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">) {
            return `${tooltipItem.dataset.label}: ${parseFormattedValueToTime(tooltipItem.formattedValue, barChartSettings.dataType)}`
          },
        },
      },
    },
    responsive: true,
    scales: {
      x: {
        stacked: true,
        title: {
          display: isLargeScreen,
          text: barChartSettings.labelX,
          color: "#94A3B8",
        },
        ticks: {
          color: "#94A3B8",
          minRotation: xAxisLabelRotation,
          maxRotation: xAxisLabelRotation,
          // TODO: Fix ticks count for backend data
          maxTicksLimit: ticks + 1,
          callback: (value: number | string) => {
            const label = data.labels[Number(value)] ?? value
            return formatMonitoringXAxisTick(label, getXAxisTickFormat(barChartSettings.labelX), timeZone)
          },
        },
      },
      y: {
        title: {
          display: isLargeScreen,
          text: `Runtime ${barChartSettings.labelY}`,
          color: "#94A3B8",
        },
        stacked: true,
        max: barChartSettings.suggestedMax,
        ticks: {
          maxTicksLimit: 6,
          color: "#94A3B8",
        },
      },
    },
  }

  return (
    <>
      {!isLargeScreen && <Title>Runtime ({barChartSettings.dataType})</Title>}
      <RuntimeBoxes barChartSettings={barChartSettings} historicalData={historicalData} />
      <div className={styles.barChartContainer}>
        <Bar data={data} options={options} plugins={[getLegendMarginPlugin(8)]} />
      </div>
    </>
  )
}

export default TemperatureBarChart
